import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import AppFeedback from "../../components/shared/AppFeedback.tsx";
import { t } from "../store-language/i18n.ts";
import { getActiveShowroom, type ActiveShowroomContent } from "./services/activeShowroom.ts";

type ShowroomState = {
  status: "loading" | "ready" | "error";
  active: ActiveShowroomContent | null;
};

type ShowroomSession = ShowroomState & { reload: () => Promise<void> };
const ActiveShowroomContext = createContext<ShowroomSession | null>(null);

/** Mounted inside the verified, keyed agency session; never persisted to storage. */
export function ActiveShowroomSessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ShowroomState>({ status: "loading", active: null });
  const request = useRef<AbortController | null>(null);

  const reload = useCallback(async function () {
    if (request.current) return;
    const controller = new AbortController();
    request.current = controller;
    setState({ status: "loading", active: null });
    try {
      const active = await getActiveShowroom(controller.signal);
      if (!controller.signal.aborted) setState({ status: "ready", active });
    } catch (error) {
      if (!controller.signal.aborted) {
        setState({ status: "error", active: null });
        AppFeedback.error({ title: t("بارگذاری نمایشگاه فعال انجام نشد.") });
      }
    } finally {
      if (request.current === controller) request.current = null;
    }
  }, []);

  useEffect(function () {
    let mounted = true;
    // Avoid issuing the discarded request during development effect replay.
    void Promise.resolve().then(function () {
      if (mounted) void reload();
    });
    return function () {
      mounted = false;
      request.current?.abort();
      request.current = null;
    };
  }, [reload]);

  const value = useMemo(function () { return { ...state, reload }; }, [state, reload]);
  return (
    <ActiveShowroomContext.Provider value={value}>
      {children}
    </ActiveShowroomContext.Provider>
  );
}

export function useActiveShowroomSession(): ShowroomSession {
  const session = useContext(ActiveShowroomContext);
  if (!session) throw new Error("ActiveShowroomSessionProvider is required.");
  return session;
}
